import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import api, { formatDate } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ShieldCheck, AlertTriangle, CheckCircle2, XCircle,
  RefreshCw, ExternalLink, Database
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

export default function DataChecksPage() {
  const { t } = useTranslation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadChecks = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/data-checks');
      setReport(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadChecks(); }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pass':
        return <CheckCircle2 className="w-5 h-5 text-green-500" />;
      case 'warn':
        return <AlertTriangle className="w-5 h-5 text-amber-500" />;
      case 'fail':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pass: 'bg-green-500/10 text-green-500 border-green-500/20',
      warn: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      fail: 'bg-red-500/10 text-red-500 border-red-500/20'
    };
    return (
      <Badge className={styles[status] || ''}>
        {status?.toUpperCase()}
      </Badge>
    );
  };

  return (
    <div data-testid="data-checks-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-barlow font-bold text-3xl text-foreground tracking-tight">
            {t('dataChecks.title')}
          </h1>
          <p className="text-muted-foreground text-sm mt-1">{t('dataChecks.subtitle')}</p>
        </div>
        <Button variant="outline" size="sm" onClick={loadChecks} disabled={loading}>
          <RefreshCw className={`w-4 h-4 me-2 ${loading ? 'animate-spin' : ''}`} />
          {t('dataChecks.runChecks')}
        </Button>
      </div>

      {loading && !report ? (
        <div className="flex items-center justify-center h-64 text-muted-foreground">
          <RefreshCw className="w-6 h-6 animate-spin me-2" />
          {t('dataChecks.running')}
        </div>
      ) : report ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                  <Database className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('dataChecks.totalChecks')}</p>
                  <p className="text-2xl font-bold text-foreground">{report.summary.total_checks}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('dataChecks.passed')}</p>
                  <p className="text-2xl font-bold text-green-500">
                    {report.summary.total_checks - report.summary.fail_count - report.summary.warn_count}
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('dataChecks.warnings')}</p>
                  <p className="text-2xl font-bold text-amber-500">{report.summary.warn_count}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-card border-border">
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-red-500/10 flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('dataChecks.failures')}</p>
                  <p className="text-2xl font-bold text-red-500">{report.summary.fail_count}</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Overall Status */}
          <Card className={`border-2 ${
            report.summary.overall_status === 'pass' ? 'border-green-500/30 bg-green-500/5' :
            report.summary.overall_status === 'warn' ? 'border-amber-500/30 bg-amber-500/5' :
            'border-red-500/30 bg-red-500/5'
          }`}>
            <CardContent className="p-4 flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                report.summary.overall_status === 'pass' ? 'bg-green-500/10' :
                report.summary.overall_status === 'warn' ? 'bg-amber-500/10' :
                'bg-red-500/10'
              }`}>
                <ShieldCheck className={`w-6 h-6 ${
                  report.summary.overall_status === 'pass' ? 'text-green-500' :
                  report.summary.overall_status === 'warn' ? 'text-amber-500' :
                  'text-red-500'
                }`} />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-foreground">{t('dataChecks.overallStatus')}</p>
                <p className="text-sm text-muted-foreground">
                  {report.summary.total_issues === 0 
                    ? t('dataChecks.allClear')
                    : t('dataChecks.issuesFound', { count: report.summary.total_issues })
                  }
                </p>
              </div>
              {getStatusBadge(report.summary.overall_status)}
            </CardContent>
          </Card>

          {/* Check Results */}
          <div className="space-y-4">
            {report.checks.map((check) => (
              <Card key={check.check} className="bg-card border-border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      {getStatusIcon(check.status)}
                      <div>
                        <h3 className="font-semibold text-foreground">{check.title}</h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {check.count === 0 
                            ? t('dataChecks.noIssues')
                            : t('dataChecks.foundCount', { count: check.count })
                          }
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {getStatusBadge(check.status)}
                      {check.link && check.count > 0 && (
                        <Link to={check.link}>
                          <Button variant="ghost" size="sm">
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </Link>
                      )}
                    </div>
                  </div>

                  {/* Show affected items if any */}
                  {check.items?.length > 0 && (
                    <div className="mt-4 bg-muted/30 rounded-lg p-3">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">
                        {t('dataChecks.affectedItems')}
                      </p>
                      <div className="space-y-1 max-h-32 overflow-y-auto">
                        {check.items.slice(0, 10).map((item, i) => (
                          <div key={i} className="text-sm text-foreground font-mono bg-background/50 px-2 py-1 rounded">
                            {item.name || item.title || item.id || JSON.stringify(item)}
                          </div>
                        ))}
                        {check.items.length > 10 && (
                          <p className="text-xs text-muted-foreground">
                            ... {t('dataChecks.andMore', { count: check.items.length - 10 })}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Timestamp */}
          <p className="text-center text-xs text-muted-foreground">
            {t('dataChecks.lastRun')}: {formatDate(report.timestamp)}
          </p>
        </>
      ) : null}
    </div>
  );
}
